import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo } from 'react';
import { useMeasure } from 'react-use';
import { deleteUndefinedValues } from '@/utils/objects';
import { selectEditingArea } from '@/common/components/Editor/hooks/useEditingAreas';
import settings, { selectionConfig, labelsSelectionConfig, colorPallets, chartColorPallet as DEFAULT_COLORS } from './settings';
import useSelections from '../../misc/useSelections';
import useSelectionLabels from '../../misc/useSelectionLabels';
import useSubscribeWidgetResults from '../hooks/useSubscribeWidgetResults';
import useData from './useData';
import { useGaugeChartStyles } from './stylesTheme';
import { buildChartRenderingHash } from '../internal/utils';
import { getDatasetsHash, getMaxGaugeWidth } from './utils';
import StyleVariables, { getSectionAttr, getSectionAttrSelector } from '../../misc/StyleVariables';
import Loading from './Loading';
import Header from '../misc/Header';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GaugeComponent = dynamic(() => import('react-gauge-component'), {
  ssr: false,
  loadableGenerated: {
    webpack: () => [require.resolveWeak('react-gauge-component')]
  }
});

const GaugeChart = _ref => {
  var _config$styles;

  let {
    widgetId,
    sectionId,
    elementId,
    reportingWidgetId,
    config,
    editing = false,
    isLoading = false,
    hasNoData = false
  } = _ref;
  const [containerRef, {
    height: containerHeight
  }] = useMeasure();
  const {
    themeStyles,
    themeCtx: {
      colorPallet: defaultColorPallet = DEFAULT_COLORS
    }
  } = useGaugeChartStyles(config);
  const {
    selections = '',
    labelsSelections = '',
    colorPallet = defaultColorPallet,
    seriesOptions = [],
    globalSeriesOptions,
    ranges,
    chartType = 'semicircle',
    isCenterTextHidden = false,
    hasGradualColors = false,
    animation = {}
  } = config;
  const results = useSubscribeWidgetResults(reportingWidgetId); // Get relevant results for each selection

  const {
    selectionResults,
    resultsUpdated
  } = useSelections({
    results,
    selections,
    selectionsLabeled: false,
    selectionConfig
  });
  const {
    selectionLabels
  } = useSelectionLabels({
    results,
    resultsUpdated,
    labelsSelections,
    selectionsLabeled: false,
    labelsSelectionConfig,
    labelsTemplate: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.labelsTemplate
  });
  const {
    chartData
  } = useData({
    results: selectionResults,
    selectionLabels,
    seriesOptions,
    globalSeriesOptions,
    ranges,
    colorPallet
  }); // Data & Loading States

  const hasDrawableData = Boolean(chartData.datasets.length); // Format Ticks

  const ticks = useMemo(() => {
    // Lookup tick label
    const formatValue = value => {
      const tick = chartData.ticks.find(tick => tick.value === value); // Default to percentage

      return (tick === null || tick === void 0 ? void 0 : tick.label) ?? `${value}%`;
    };

    return chartData.ticks.map(tick => {
      return {
        value: tick.value,
        valueConfig: {
          formatTextValue: formatValue,
          hide: !tick.label
        },
        lineConfig: {
          hide: !tick.label
        }
      };
    });
  }, [chartData.ticks]);
  const nameSpace = `${widgetId}-${sectionId}-gauge-chart-${chartType}`;
  const gaugeChartHash = `${nameSpace}-${ranges === null || ranges === void 0 ? void 0 : ranges.length}-${chartData.value}` + `-${isCenterTextHidden}-${hasGradualColors}-${globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.template}` + colorPallet.join('.') + chartData.label + getDatasetsHash(chartData.datasets) + buildChartRenderingHash({
    animation,
    themeStyles
  }); // Detect clicks on the arc of the banded gauge
  // and set the slice as the active editing area.
  // Note: The click event only triggers after the gauge is animated.

  useEffect(() => {
    if (!editing || chartType !== 'grafana') return;

    const clickHandler = event => {
      const target = event.target;
      if (!target.closest(`#${nameSpace}`)) return;
      if (!target.closest('.subArc')) return;
      event.stopPropagation();
      setTimeout(() => {
        setSliceAsEditingArea({
          widgetId,
          sectionId,
          selectionIndex: chartData.selectionIndex,
          selection: chartData.selection
        }, event);
      });
    };

    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [editing, chartType, nameSpace, gaugeChartHash, chartData, sectionId, widgetId]);
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({}, elementId && {
    id: elementId
  }), {}, {
    className: styles.container
  }, getSectionAttr(widgetId, sectionId)), {}, {
    children: [/*#__PURE__*/_jsx(Header, {
      widgetId: widgetId,
      sectionId: sectionId,
      editing: editing,
      config: config,
      className: styles.header
    }), isLoading || hasNoData || !hasDrawableData ? /*#__PURE__*/_jsx(Loading, {
      editing: editing
    }) : /*#__PURE__*/_jsx("div", {
      className: clsx(styles.main),
      ref: containerRef,
      style: {
        maxWidth: getMaxGaugeWidth(chartType, containerHeight)
      },
      children: /*#__PURE__*/_jsx(GaugeComponent, {
        id: nameSpace,
        type: chartType,
        value: chartData.value,
        style: deleteUndefinedValues({
          background: (config === null || config === void 0 ? void 0 : (_config$styles = config.styles) === null || _config$styles === void 0 ? void 0 : _config$styles.backgroundColor) || themeStyles.chartBackgroundColor,
          fontFamily: themeStyles.chartAxisFontFamily
        }),
        marginInPercent: themeStyles.gaugeInnerMargin,
        labels: {
          valueLabel: {
            formatTextValue: value => {
              if (chartData.label) return chartData.label;

              if (chartData.valueTemplate) {
                return chartData.valueTemplate;
              }

              return `${value}%`;
            },
            style: {
              textShadow: 'none',
              fill: themeStyles.titleColor || themeStyles.headingFontColor,
              fontFamily: themeStyles.titleFontFamily,
              fontWeight: themeStyles.titleFontWeight,
              fontSize: themeStyles.titleFontSize
            },
            hide: config.isCenterTextHidden
          },
          tickLabels: {
            ticks: ticks,
            defaultTickValueConfig: {
              style: {
                fontSize: themeStyles.chartAxisFontSize,
                fontWeight: themeStyles.chartAxisFontWeight,
                fill: themeStyles.chartAxisFontColor
              }
            }
          }
        },
        arc: {
          nbSubArcs: config.hasGradualColors ? 80 : undefined,
          gradient: hasGradualColors && chartType === 'grafana' ? false : hasGradualColors,
          // @ts-expect-error on click event is missing in react-gauge-component types
          subArcs: withActions({
            widgetId,
            sectionId,
            chartData,
            editing
          }),
          padding: 0,
          width: themeStyles.arcWidth,
          cornerRadius: 0,
          emptyColor: themeStyles.arcEmptyColor
        },
        pointer: {
          type: 'needle',
          width: 5,
          length: 0.8,
          color: themeStyles.pointerColor,
          animate: animation.enabled ?? true,
          animationDelay: animation.delay,
          animationDuration: animation.duration
        }
      }, gaugeChartHash)
    }), /*#__PURE__*/_jsx(StyleVariables, {
      selector: getSectionAttrSelector(widgetId, sectionId),
      data: _objectSpread(_objectSpread({}, (settings === null || settings === void 0 ? void 0 : settings.styles) || {}), (config === null || config === void 0 ? void 0 : config.styles) || {})
    })]
  }));
};

export default GaugeChart;
export { DEFAULT_COLORS, selectionConfig, labelsSelectionConfig, colorPallets, useData }; // Add click actions to each arc of the gauge chart

function withActions(params) {
  const {
    widgetId,
    sectionId,
    chartData,
    editing
  } = params;
  const datasets = chartData.datasets;
  return datasets.map(dataset => {
    return _objectSpread(_objectSpread({}, dataset), editing ? {
      onClick: event => {
        setTimeout(() => {
          setSliceAsEditingArea({
            widgetId,
            sectionId,
            selectionIndex: chartData.selectionIndex,
            selection: chartData.selection
          }, event);
        });
      }
    } : {});
  });
} // Apply user selections of an individual
// chart slice as the active editing area


function setSliceAsEditingArea(_ref2, event) {
  let {
    widgetId,
    sectionId,
    selectionIndex,
    selection
  } = _ref2;
  if (selectionIndex < 0) return; // @ts-ignore

  const clientX = event === null || event === void 0 ? void 0 : event.clientX; // @ts-ignore

  const clientY = (event === null || event === void 0 ? void 0 : event.clientY) || null;
  const hasClickPosition = typeof clientX === 'number' && typeof clientY === 'number'; // Send selection

  selectEditingArea({
    widgetId,
    sectionId,
    sectionEditingScope: 'selections',
    sectionEditingArea: `item-${selectionIndex}`,
    clickPosition: hasClickPosition ? {
      x: clientX,
      y: clientY
    } : undefined,
    editingAreaMeta: JSON.stringify({
      selection,
      selectionIndex
    })
  });
}